import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ApolloError } from '@apollo/client';
import { Subject, Subscription, takeUntil } from 'rxjs';

import {
	apolloErrorContains,
	nonRetryableErrorSubStrings,
	SubscriptionManager,
	SubscriptionManagerConfigCreate
} from '@shure/cloud/shared/apollo';
import { FEATURE_FLAG_SERVICE, CloudFeatureFlagsService } from '@shure/cloud/shared/utils/feature-flags';
import { ILogger } from '@shure/shared/angular/utils/logging';

import {
	AudioChannelSubscriptionGQL,
	AuracastAudioChannelFeatureFragment,
	AuracastEncryptionAudioChannelFeatureFragment,
	NodeChangeType
} from './graphql/generated/cloud-sys-api';
import { AllAuracastAudioChannelFeatureFragments } from './mappers/map-auracast';

export interface DeviceWithAudioChannels {
	id: string;
	features: {
		audioChannels?: {
			audioChannels: Array<{
				id: string;
				description: {
					features: {
						auracast?: { supported: boolean } | null;
					};
				};
				features: AuracastAudioChannelFeatureFragment & AuracastEncryptionAudioChannelFeatureFragment;
			}>;
		} | null;
	};
}

@Injectable({ providedIn: 'root' })
export class SysApiAudioChannelService {
	private readonly audioChannelSubscriptionGQL = inject(AudioChannelSubscriptionGQL);
	private readonly logger = inject(ILogger).createScopedLogger('SysApiAudioChannelService');
	private readonly featureFlagsService = inject<CloudFeatureFlagsService>(FEATURE_FLAG_SERVICE);

	private readonly auracastFeatureEnabled = toSignal(this.featureFlagsService.getFeatureFlag$('cdm-auracast'), {
		initialValue: false
	});

	private destroy$ = new Subject<void>();

	// Tracks which audio channel IDs are associated with each device so that
	// per-device changes do not clobber subscriptions belonging to other devices.
	private readonly deviceAudioChannelMap = new Map<string, string[]>();

	private readonly audioChannelsSubscriptionManager = new SubscriptionManager({
		subscriptionType: 'inventory-devices',
		create: (config): Subscription => this.createAudioChannelSubscription(config),
		retryWaitMs: 10_000,
		maxRetryAttempts: 3
	});

	public initialize(): void {
		this.destroy$ = new Subject<void>();
	}

	public suspend(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.audioChannelsSubscriptionManager.deregisterAll();
		this.deviceAudioChannelMap.clear();
	}

	/**
	 * Resolves the active Auracast audio channel features for a device.
	 * Also maintains audio channel subscriptions, registering new channels and
	 * deregistering channels that are no longer present on the device.
	 */
	public handleAuracastChannels(
		deviceId: string,
		deviceFragment: DeviceWithAudioChannels
	): AllAuracastAudioChannelFeatureFragments | null {
		const auracastAudioChannelIds = this.getAuracastAudioChannelIds(deviceFragment);
		this.updateAudioChannelSubscriptions(deviceId, auracastAudioChannelIds);

		const auracastChannelIds = this.deviceAudioChannelMap.get(deviceId) ?? [];
		// For now, we only support 1 auracast channel per device.
		// We still get the IDs as an array in case that changes in the future.
		const auracastChannelId = auracastChannelIds.length > 0 ? auracastChannelIds[0] : null;
		const matchingAudioChannel = deviceFragment.features.audioChannels?.audioChannels.find(
			(channel) => channel.id === auracastChannelId
		);
		const auracastFeatures: AllAuracastAudioChannelFeatureFragments | null = matchingAudioChannel
			? {
					channelId: matchingAudioChannel.id,
					...matchingAudioChannel.features
				}
			: null;
		return auracastFeatures;
	}

	/**
	 * Deregisters audio channel subscriptions that were registered on behalf of the
	 * given device. Channels still referenced by other devices are left intact.
	 */
	public removeAudioChannelSubscriptions(deviceId: string): void {
		const channelIds = this.deviceAudioChannelMap.get(deviceId) ?? [];
		this.deviceAudioChannelMap.delete(deviceId);

		for (const channelId of channelIds) {
			const stillInUse = Array.from(this.deviceAudioChannelMap.values()).some((ids) => ids.includes(channelId));
			if (!stillInUse) {
				this.audioChannelsSubscriptionManager.deregister(channelId);
			}
		}
	}

	private getAuracastAudioChannelIds(fragmentData: DeviceWithAudioChannels): string[] {
		if (!fragmentData.features.audioChannels) {
			return [];
		}
		return (
			fragmentData.features.audioChannels.audioChannels
				.filter((channel) => channel.description.features.auracast?.supported)
				.map((channel) => channel.id) ?? []
		);
	}

	private updateAudioChannelSubscriptions(deviceId: string, updatedChannelIds: string[]): void {
		const previousChannelIds = this.deviceAudioChannelMap.get(deviceId) ?? [];

		// Deregister channels that this device no longer needs.
		const removedIds = previousChannelIds.filter((id) => !updatedChannelIds.includes(id));
		for (const removedId of removedIds) {
			this.audioChannelsSubscriptionManager.deregister(removedId);
		}

		// Register any new channel IDs (SubscriptionManager ignores duplicates).
		if (updatedChannelIds.length > 0) {
			this.audioChannelsSubscriptionManager.register(updatedChannelIds);
			this.deviceAudioChannelMap.set(deviceId, updatedChannelIds);
		} else {
			this.deviceAudioChannelMap.delete(deviceId);
		}
	}

	private createAudioChannelSubscription({ id, retryCallback }: SubscriptionManagerConfigCreate): Subscription {
		const subscriptionTypes: NodeChangeType[] = [];

		if (this.auracastFeatureEnabled()) {
			subscriptionTypes.push(NodeChangeType.AudioChannelAuracast);
			subscriptionTypes.push(NodeChangeType.AudioChannelAuracastEncryption);
		}

		return this.audioChannelSubscriptionGQL
			.subscribe(
				{
					nodeId: id,
					audioChannelNodeChangeTypes: subscriptionTypes
				},
				{ fetchPolicy: 'network-only', errorPolicy: 'ignore' }
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (change) => {
					this.logger.trace('audioChannelSubscriptionGQL', 'Received update', { id, change });
				},
				complete: () => {
					this.logger.debug('audioChannelSubscriptionGQL', 'Subscription completed. Will retry.', {
						nodeId: id
					});
					retryCallback();
				},
				error: (error: ApolloError) => {
					if (apolloErrorContains(error, nonRetryableErrorSubStrings)) {
						return;
					}
					this.logger.error(
						'audioChannelSubscriptionGQL',
						'Encountered retryable error',
						JSON.stringify({ id, error })
					);
					retryCallback();
				}
			});
	}
}
